import {
  ActionBpContext,
  buildActionBpContext,
  buildLiveBpContext,
  buildProductAnalyticsBpContext,
  buildTeamBpContext,
  buildTeamBpContextFromTeamUser,
  buildViewBpContext,
  buildWebBpContext,
  buildThirdPartyIntegrationBpContext,
  buildVenueBpContext,
  LiveBpContext,
  PartialWithRequired,
  ProductAnalyticsBpContext,
  ViewBpContext,
  WebBpContext,
} from '@vimeo/use-tracking';

import { AudienceType, EventPrivacy } from './types';
import { pageNameFromPathname } from './utils';
import { liveTrackingConfig } from '../../configs/LiveTrackingConfig';

export function newWebCtx(fields?: Partial<WebBpContext>) {
  const location = fields?.location ?? null;
  const target = fields?.target ?? null;
  const copy = fields?.copy ?? null;
  const target_path = fields?.target_path ?? null;

  const referrerPathname = document.referrer ? new URL(document.referrer).pathname : '';

  return buildWebBpContext({
    location,
    page_name: pageNameFromPathname(window.location.pathname),
    referrer_page_name: referrerPathname
      ? (pageNameFromPathname(referrerPathname) as WebBpContext['referrer_page_name'])
      : null,
    referrer: document.referrer,
    path: window.location.pathname,
    target,
    target_path,
    copy,
  });
}

export function newViewCtx(viewType: ViewBpContext['view_type']) {
  return buildViewBpContext({ view_type: viewType, feature: null });
}

export function newActionCtx(actionType: ActionBpContext['action_type']) {
  return buildActionBpContext({ action_type: actionType, feature: null });
}

export function newThirdPartyIntegrationCtx() {
  return buildThirdPartyIntegrationBpContext({
    integration_id: null,
    integration_name: null,
    is_partner: null,
  });
}

export function newProductAnalyticsCtx(
  fields: PartialWithRequired<ProductAnalyticsBpContext, 'feature' | 'location' | 'product'>,
) {
  return buildProductAnalyticsBpContext(fields);
}

export function newTeamCtx() {
  const { teamUser, teamOwnerId, teamAccountType } =
    liveTrackingConfig.BIG_PICTURE_TEAM_CONTEXT_BASE;

  return teamUser
    ? buildTeamBpContextFromTeamUser(teamUser)
    : buildTeamBpContext({
        is_team_member: false,
        team_role: 'owner',
        team_owner_id: (teamOwnerId?.valueOf() as number) ?? null,
        team_subscription_type: (teamAccountType?.valueOf() as string) ?? null,
      });
}

export function newLiveCtx(fields: PartialWithRequired<LiveBpContext, 'live_feature'>) {
  const {
    BIG_PICTURE_LIVE_SCHEMA_BASE: { liveStatus, liveEventId, liveEventType, leadSource },
    DETAILS: { SESSION_ID, SESSION_TYPE },
    BIG_PICTURE_EVENT_SETTINGS_SCHEMA_BASE: { eventPrivacy, productionMethod },
  } = liveTrackingConfig;

  return buildLiveBpContext({
    live_production_method: productionMethod
      ? (productionMethod as LiveBpContext['live_production_method'])
      : null,
    audience_type: SESSION_TYPE ? AudienceType[SESSION_TYPE] : null,
    event_privacy: eventPrivacy ? (EventPrivacy as any)[eventPrivacy] : null,
    recurring_live_event_id: SESSION_ID?.valueOf()?.toString(),
    encoder_type: productionMethod,
    lead_source: leadSource,
    live_status: liveStatus ? (liveStatus as LiveBpContext['live_status']) : 'unknown',
    live_event_type: liveEventType
      ? (liveEventType as LiveBpContext['live_event_type'])
      : 'live_event',
    live_event_id: liveEventId?.toString() ?? '',
    ...fields,
  });
}

export function newVenueCtx() {
  const {
    DETAILS: { SESSION_ID },
    BIG_PICTURE_VENUE_SCHEMA_BASE: {
      venueId,
      ownerId,
      spaceId,
      registrantId,
      sessionId,
      isSpacePrivate,
      isSpaceBreakout,
      recordingType,
    },
  } = liveTrackingConfig;

  return buildVenueBpContext({
    venue_id: venueId,
    owner_id: ownerId,
    space_id: spaceId,
    registrant_id: registrantId,
    session_id: sessionId ? sessionId : (SESSION_ID?.valueOf()?.toString() as string),
    is_space_private: isSpacePrivate,
    is_space_breakout: isSpaceBreakout,
    recording_type: recordingType || null,
  });
}
